import { useEffect, useState } from 'react'
import './SettlementSubmitConfirmModal.css'

export const SETTLEMENT_SUBMIT_LEGAL_INTRO =
  'Odesláním tohoto vyúčtování pracovní cesty vedoucímu zaměstnance prohlašujete a potvrzujete, že:'

export const SETTLEMENT_SUBMIT_LEGAL_POINTS = [
  'všechny údaje uvedené ve vyúčtování — včetně tabulky denních záznamů, částek náhrad, údajů o dopravě, přiložených dokladů a zprávy o výsledku pracovní cesty — jsou úplné, pravdivé a odpovídají skutečnosti;',
  'náklady byly vynaloženy výhradně v souvislosti s touto pracovní cestou, v rozsahu schváleného cestovního příkazu a v souladu s platnými vnitřními předpisy zaměstnavatele a právními předpisy, zejména zákonem č. 262/2006 Sb., o cestovních náhradách;',
  'přiložené doklady, pokud jsou požadovány, řádně prokazují uvedené výdaje a nebyly již uplatněny jinde;',
  'jste si vědomi, že nepravdivé, neúplné nebo zkreslené údaje mohou mít důsledky v pracovněprávním, disciplinárním a případně trestněprávním smyslu, a že odeslané údaje budou použity pro schválení vedoucím a následné zpracování hlavním ekonomem.',
] as const

export const SETTLEMENT_SUBMIT_LEGAL_FOOTER =
  'Po odeslání nebude možné vyúčtování jako zaměstnanec dále upravovat; případné opravy probíhají prostřednictvím vedoucího nebo po jeho schválení u hlavního ekonoma.'

interface SettlementSubmitConfirmModalProps {
  isOpen: boolean
  orderNumber?: string
  loading?: boolean
  errorMessage?: string
  onClose: () => void
  onConfirm: () => void
}

export function SettlementSubmitConfirmModal({
  isOpen,
  orderNumber,
  loading = false,
  errorMessage,
  onClose,
  onConfirm,
}: SettlementSubmitConfirmModalProps) {
  const [agreed, setAgreed] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setAgreed(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, loading, onClose])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreed || loading) return
    onConfirm()
  }

  return (
    <div
      className="settlement-submit-modal-overlay"
      role="presentation"
      onClick={() => {
        if (!loading) onClose()
      }}
    >
      <div
        className="settlement-submit-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settlement-submit-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="settlement-submit-modal-header">
          <h2 id="settlement-submit-modal-title">Potvrzení před odesláním vyúčtování</h2>
          <button
            type="button"
            className="settlement-submit-modal-close"
            onClick={onClose}
            disabled={loading}
            title="Zavřít"
            aria-label="Zavřít"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settlement-submit-modal-body">
            {errorMessage && (
              <div className="settlement-submit-error" role="alert">
                <strong>Vyúčtování nelze odeslat:</strong> {errorMessage}
              </div>
            )}
            {orderNumber && (
              <p className="settlement-submit-order-ref">
                <strong>Cestovní příkaz č.:</strong> {orderNumber}
              </p>
            )}
            <p className="settlement-submit-intro">{SETTLEMENT_SUBMIT_LEGAL_INTRO}</p>
            <ul className="settlement-submit-legal-list">
              {SETTLEMENT_SUBMIT_LEGAL_POINTS.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <p className="settlement-submit-footer-note">{SETTLEMENT_SUBMIT_LEGAL_FOOTER}</p>
            <label className="settlement-submit-checkbox">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={loading}
              />
              <span>
                Seznámil/a jsem se s výše uvedeným, beru na vědomí svou odpovědnost za pravdivost údajů a
                souhlasím s odesláním vyúčtování vedoucímu k dalšímu schválení.
              </span>
            </label>
          </div>
          <div className="settlement-submit-modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              Zrušit
            </button>
            <button type="submit" className="btn-primary" disabled={!agreed || loading}>
              {loading ? 'Odesílání…' : 'Odeslat vedoucímu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
